/**
 * useInterviewSession — state machine consuming /api/subject/turn SSE bursts.
 *
 * One hook drives the entire subject-side interview:
 *   - Holds the current question + transcript history (questions emitted so far)
 *   - Submits answers (audio blob → transcribe → answer event in next turn) OR typed text
 *   - Listens for terminate / superseded / token_expired / cost_cap_hit events
 *   - Surfaces high-level UI states: bootstrapping | awaiting_answer | thinking | reading_question | terminated | error
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { postTurn, transcribeAudio, type OutboundEvent } from "../lib/subject-api";

export type InterviewUiState =
  | "bootstrapping"
  | "thinking"
  | "reading_question"
  | "awaiting_answer"
  | "submitting_answer"
  | "terminated"
  | "error";

export type QuestionRecord = {
  id: string;
  orderIndex: number;
  text: string;
};

export type AnswerRecord = {
  questionId: string;
  text: string;
  lang: string;
  typed: boolean;
};

export type SessionError = {
  kind: "superseded" | "token_expired" | "cost_cap_hit" | "transport" | "transcribe_failed" | "unknown";
  detail?: string;
};

export type UseInterviewSessionReturn = {
  uiState: InterviewUiState;
  currentQuestion: QuestionRecord | null;
  streamingText: string;
  questions: QuestionRecord[];
  answers: AnswerRecord[];
  terminationReason: string | null;
  error: SessionError | null;
  bootstrap: () => Promise<void>;
  submitAudioAnswer: (blob: Blob, durationMs: number) => Promise<void>;
  submitTypedAnswer: (text: string, lang?: string) => Promise<void>;
  done: () => Promise<void>;
};

export function useInterviewSession(): UseInterviewSessionReturn {
  const [uiState, setUiState] = useState<InterviewUiState>("bootstrapping");
  const [currentQuestion, setCurrentQuestion] = useState<QuestionRecord | null>(null);
  const [streamingText, setStreamingText] = useState("");
  const [questions, setQuestions] = useState<QuestionRecord[]>([]);
  const [answers, setAnswers] = useState<AnswerRecord[]>([]);
  const [terminationReason, setTerminationReason] = useState<string | null>(null);
  const [error, setError] = useState<SessionError | null>(null);

  const activeStream = useRef<AbortController | null>(null);

  const consumeStream = useCallback(
    async (eventGen: AsyncIterable<OutboundEvent>) => {
      setUiState("thinking");
      setStreamingText("");

      for await (const event of eventGen) {
        if (event.type === "thinking") {
          setUiState("thinking");
        } else if (event.type === "question_delta") {
          setStreamingText((prev) => prev + event.text);
          setUiState("reading_question"); // first delta flips us out of thinking
        } else if (event.type === "question") {
          const q: QuestionRecord = {
            id: event.questionId,
            orderIndex: event.orderIndex,
            text: event.text,
          };
          setQuestions((prev) => [...prev, q]);
          setCurrentQuestion(q);
          setStreamingText(""); // settled: typewriter takes over from text prop
          setUiState("reading_question");
        } else if (event.type === "terminate") {
          setTerminationReason(event.reason);
          setUiState("terminated");
        } else if (event.type === "superseded") {
          setError({ kind: "superseded" });
          setUiState("error");
        } else if (event.type === "token_expired") {
          setError({ kind: "token_expired" });
          setUiState("error");
        } else if (event.type === "cost_cap_hit") {
          setError({ kind: "cost_cap_hit", detail: event.kind });
        } else if (event.type === "error") {
          setError({ kind: "unknown", detail: event.message });
          setUiState("error");
        }
      }
    },
    [],
  );

  const bootstrap = useCallback(async () => {
    setUiState("bootstrapping");
    setError(null);
    await consumeStream(postTurn({ type: "bootstrap" }));
  }, [consumeStream]);

  const submitAudioAnswer = useCallback(
    async (blob: Blob, _durationMs: number) => {
      if (!currentQuestion) {
        setError({ kind: "unknown", detail: "no_current_question" });
        return;
      }
      setUiState("submitting_answer");

      const t = await transcribeAudio(blob);
      if (!t.ok) {
        setError({ kind: "transcribe_failed", detail: t.error });
        setUiState("error");
        return;
      }

      const answer: AnswerRecord = {
        questionId: currentQuestion.id,
        text: t.text,
        lang: t.language,
        typed: false,
      };
      setAnswers((prev) => [...prev, answer]);

      await consumeStream(
        postTurn({
          type: "answer",
          questionId: currentQuestion.id,
          text: t.text,
          lang: t.language,
        }),
      );
    },
    [currentQuestion, consumeStream],
  );

  const submitTypedAnswer = useCallback(
    async (text: string, lang?: string) => {
      if (!currentQuestion) {
        setError({ kind: "unknown", detail: "no_current_question" });
        return;
      }
      setUiState("submitting_answer");
      const language = lang ?? "en";
      const answer: AnswerRecord = {
        questionId: currentQuestion.id,
        text,
        lang: language,
        typed: true,
      };
      setAnswers((prev) => [...prev, answer]);

      await consumeStream(
        postTurn({
          type: "answer",
          questionId: currentQuestion.id,
          text,
          lang: language,
          typed: true,
        }),
      );
    },
    [currentQuestion, consumeStream],
  );

  const done = useCallback(async () => {
    setUiState("submitting_answer");
    await consumeStream(postTurn({ type: "user_done" }));
  }, [consumeStream]);

  useEffect(() => {
    return () => {
      if (activeStream.current) activeStream.current.abort();
    };
  }, []);

  return {
    uiState,
    currentQuestion,
    streamingText,
    questions,
    answers,
    terminationReason,
    error,
    bootstrap,
    submitAudioAnswer,
    submitTypedAnswer,
    done,
  };
}
